import { useMemo } from "react";
import moment from "moment-timezone";
import { useScheduleContext } from "../context/useScheduleContext";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { useCompany } from "../../../../../utils/hooks/company";
import { useAddedPayCode } from "../../../../TimeSheet/components/TimeSheetTable/hooks/useAddedPayCode";
import { getPrimaryActiveResource } from "../../../../../utils/helpers/user";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { getAutomaticBreaks } from "../../helpers/form/sections";
import { useRowGroupBy } from "./useRowGroupBy";
import type { Resource } from "../../types/Resource";

export const useDefaultSection = () => {
    const company = useCompany();
    const [{ mode }] = useFilters();
    const { selectedCellOrEvent } = useScheduleContext();
    const { allocatedGroups } = useGroups();
    const rowGroupBy = useRowGroupBy();
    const defaultPayCode = useAddedPayCode(selectedCellOrEvent?.user);
    const primaryJob = getPrimaryActiveResource((selectedCellOrEvent?.user as Resource)?.jobs || []);
    const primaryLocation = getPrimaryActiveResource((selectedCellOrEvent?.user as Resource)?.locations || []);

    const levels = useMemo(() => {
        return allocatedGroups?.reduce((total: any, group: any) => {
            const level = group?.level.replace("_", "");
            if (selectedCellOrEvent?.user && (selectedCellOrEvent?.user as any)?.[level]) {
                total[level] = (selectedCellOrEvent?.user as any)[level];
            }
            return total;
        }, {});
    }, [allocatedGroups, selectedCellOrEvent?.user]);

    return useMemo(() => {
        const calcGroup = (selectedCellOrEvent?.user as Resource)?.calculationGroup;
        const scheduleSettings = calcGroup?.schedule || company?.settings?.schedule;
        const shiftStart = scheduleSettings?.shiftStart
            ? moment.parseZone(scheduleSettings.shiftStart)
            : moment().startOf("day").hour(9);
        const shiftEnd = scheduleSettings?.shiftEnd
            ? moment.parseZone(scheduleSettings.shiftEnd)
            : moment().startOf("day").hour(17);

        let start = selectedCellOrEvent?.startDate
            ? combineDateAndTime(moment(selectedCellOrEvent?.date), moment(selectedCellOrEvent.startDate))
            : undefined;
        let end = selectedCellOrEvent?.endDate
            ? combineDateAndTime(moment(selectedCellOrEvent?.date), moment(selectedCellOrEvent.endDate))
            : undefined;

        if (mode !== "day" && selectedCellOrEvent?.isCreating) {
            start = combineDateAndTime(moment(selectedCellOrEvent?.date), shiftStart);
            end = combineDateAndTime(moment(selectedCellOrEvent?.date), shiftEnd);
        }

        const duration = start && end ? moment(end).diff(moment(start), "seconds") : 0;

        const defaultSection = {
            type: "regular",
            payCode: defaultPayCode,
            location: primaryLocation,
            job: primaryJob,
            duration: renderDurationAsFormat(duration, "HH:mm"),
            start,
            end,
            ...levels,
            ...(rowGroupBy ? { [rowGroupBy.field]: rowGroupBy.value } : {}),
        };

        if (selectedCellOrEvent?.isUnassignedShift && selectedCellOrEvent?.isCreating) {
            return {
                defaultSection: { isSimple: true },
                breaks: []
            };
        }

        // Use the same automatic-break calculation used everywhere else (sections
        // recompute, save) so the breaks shown on open match what actually gets saved.
        const breaks = calcGroup?.breaks?.automatic?.status
            ? (getAutomaticBreaks(calcGroup?.breaks, [defaultSection], defaultSection) || [])
            : [];

        return { defaultSection, breaks };
    }, [
        selectedCellOrEvent,
        company?.settings?.schedule,
        mode,
        defaultPayCode,
        primaryLocation,
        primaryJob,
        levels,
        rowGroupBy
    ]);
};
